import type { RehearsalQuestion, ScoreLabel } from '@/types';

interface Props {
  questions: RehearsalQuestion[];
  overallScore: number | null;
  onPracticeAgain: () => void;
  onViewDetails?: () => void;
}

const SCORE_COLORS: Record<string, { bg: string; text: string; bar: string }> = {
  excellent: { bg: 'bg-green-50', text: 'text-green-700', bar: 'bg-green-500' },
  good: { bg: 'bg-blue-50', text: 'text-blue-700', bar: 'bg-blue-500' },
  acceptable: { bg: 'bg-yellow-50', text: 'text-yellow-700', bar: 'bg-yellow-500' },
  needsWork: { bg: 'bg-orange-50', text: 'text-orange-700', bar: 'bg-orange-500' },
  missed: { bg: 'bg-red-50', text: 'text-red-700', bar: 'bg-red-500' },
};

function getScoreCategory(score: number | null): keyof typeof SCORE_COLORS {
  if (score === null) return 'missed';
  if (score >= 90) return 'excellent';
  if (score >= 75) return 'good';
  if (score >= 60) return 'acceptable';
  if (score >= 40) return 'needsWork';
  return 'missed';
}

function getScoreLabel(score: number | null): ScoreLabel {
  if (score === null) return 'Missed';
  if (score >= 90) return 'Excellent';
  if (score >= 75) return 'Good';
  if (score >= 60) return 'Acceptable';
  if (score >= 40) return 'Needs Work';
  return 'Missed';
}

export function RehearsalSummary({ questions, overallScore, onPracticeAgain, onViewDetails }: Props) {
  const scoreCategory = getScoreCategory(overallScore);
  const scoreColors = SCORE_COLORS[scoreCategory];
  const scoreLabel = getScoreLabel(overallScore);

  // Calculate stats
  const answeredQuestions = questions.filter(q => !q.skipped && q.score !== undefined);
  const skippedQuestions = questions.filter(q => q.skipped);

  const excellentCount = answeredQuestions.filter(q => (q.score ?? 0) >= 90).length;
  const goodCount = answeredQuestions.filter(q => (q.score ?? 0) >= 75 && (q.score ?? 0) < 90).length;
  const acceptableCount = answeredQuestions.filter(q => (q.score ?? 0) >= 60 && (q.score ?? 0) < 75).length;
  const needsWorkCount = answeredQuestions.filter(q => (q.score ?? 0) < 60).length;

  // Find weak areas (lowest scoring questions)
  const sortedByScore = [...answeredQuestions].sort((a, b) => (a.score ?? 0) - (b.score ?? 0));
  const weakAreas = sortedByScore.slice(0, 3).filter(q => (q.score ?? 0) < 75);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-lg font-bold text-gray-800">Rehearsal Complete</h3>
        <p className="text-sm text-gray-500">Here's how you did</p>
      </div>

      {/* Overall Score */}
      <div className={`${scoreColors.bg} rounded-xl p-4 text-center`}>
        <div className={`text-4xl font-bold ${scoreColors.text}`}>
          {overallScore !== null ? Math.round(overallScore) : '--'}/100
        </div>
        <div className={`text-sm font-medium ${scoreColors.text} mt-1`}>{scoreLabel}</div>

        {/* Progress Bar */}
        <div className="mt-3 h-3 bg-white/50 rounded-full overflow-hidden">
          <div
            className={`h-full ${scoreColors.bar} transition-all duration-500`}
            style={{ width: `${overallScore ?? 0}%` }}
          />
        </div>
      </div>

      {/* Questions Breakdown */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-gray-700">{questions.length}</div>
          <div className="text-xs text-gray-500">Questions</div>
        </div>
        <div className="bg-gray-50 rounded-lg p-3 text-center">
          <div className="text-2xl font-bold text-gray-700">{answeredQuestions.length}</div>
          <div className="text-xs text-gray-500">Answered</div>
        </div>
      </div>

      {/* Score Distribution */}
      <div className="space-y-2">
        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
          Score Distribution
        </div>
        <div className="grid grid-cols-4 gap-2 text-center">
          <div className="bg-green-50 rounded-lg p-2">
            <div className="text-lg font-bold text-green-700">{excellentCount}</div>
            <div className="text-xs text-green-600">Excellent</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-2">
            <div className="text-lg font-bold text-blue-700">{goodCount}</div>
            <div className="text-xs text-blue-600">Good</div>
          </div>
          <div className="bg-yellow-50 rounded-lg p-2">
            <div className="text-lg font-bold text-yellow-700">{acceptableCount + needsWorkCount}</div>
            <div className="text-xs text-yellow-600">Needs Work</div>
          </div>
          <div className="bg-gray-100 rounded-lg p-2">
            <div className="text-lg font-bold text-gray-600">{skippedQuestions.length}</div>
            <div className="text-xs text-gray-500">Skipped</div>
          </div>
        </div>
      </div>

      {/* Areas to Review */}
      {weakAreas.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Areas to Review
          </div>
          <div className="space-y-2">
            {weakAreas.map((q) => (
              <div key={q.id} className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                <div className="flex items-start gap-2">
                  <span className="text-orange-500 text-sm">⚠️</span>
                  <div className="flex-1">
                    <p className="text-sm text-gray-700 line-clamp-2">{q.questionText}</p>
                    {q.feedback && (
                      <p className="text-xs text-orange-600 mt-1">{q.feedback}</p>
                    )}
                  </div>
                  <span className="text-sm font-bold text-orange-600">{Math.round(q.score ?? 0)}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        {onViewDetails && (
          <button
            onClick={onViewDetails}
            className="flex-1 py-2.5 px-4 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            View All Answers
          </button>
        )}
        <button
          onClick={onPracticeAgain}
          className="flex-1 py-2.5 px-4 text-sm font-medium text-white bg-blue-500 rounded-lg hover:bg-blue-600 transition-colors"
        >
          Practice Again
        </button>
      </div>
    </div>
  );
}
